/**
 * Runtime registry mapping a pasted URL to the context reference it names.
 *
 * The composer already turns a picked `@`-mention into a context chip
 * (`@[title](github-pr:owner/repo#42)`), which the server expands into the
 * item's real content before the agent sees the prompt. Pasting the item's URL
 * should reach the same place — a link is how people actually carry a pull
 * request or a ticket between tabs — but the composer lives in `plugin-chat`
 * and must not know what a GitHub URL looks like.
 *
 * So each plugin declares the URL shape alongside the context provider that
 * already owns the kind (`agentContextProviders[].urlPattern` in its web
 * manifest), `web` compiles those into this registry, and the composer asks
 * the registry. Same seam, and same layering rule, as
 * {@link ./content-scanners} — which is the read-side twin: that one turns a
 * URL in *rendered output* into a badge, this one turns a URL *pasted into the
 * composer* into a reference.
 */

export interface ContextUrlMatcherDescriptor {
  /** Stable identifier, unique across all registered matchers. */
  id: string;
  /** The context reference kind produced (e.g. `github-pr`). */
  kind: string;
  /**
   * URL pattern. Its match is replaced by the chip, so it must cover the whole
   * URL. Capture groups feed the templates below. Normalized on registration:
   * the `g`/`y` flags are stripped, since matching walks the text itself.
   */
  regex: RegExp;
  /**
   * The reference id, as a template over the capture groups (`$1`, `$2`, …).
   * For GitHub PRs, `$1/$2#$3` builds the canonical `owner/repo#42`. It is
   * also the chip's label: a URL carries an item's identity, never its title
   * (that only arrives once something resolves the item).
   */
  idTemplate: string;
  /** Lower sorts earlier; ties break by id. Defaults to 0. */
  order?: number;
}

/** A context reference recovered from a URL. */
interface ContextUrlMatch {
  kind: string;
  id: string;
  title: string;
}

const matchers = new Map<string, ContextUrlMatcherDescriptor>();
let snapshot: ContextUrlMatcherDescriptor[] = [];

function rebuildSnapshot(): void {
  snapshot = [...matchers.values()].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0) || a.id.localeCompare(b.id),
  );
}

/** Strip `g`/`y` so a shared regex's `lastIndex` can't leak between calls. */
function normalizeRegex(regex: RegExp): RegExp {
  return new RegExp(regex.source, regex.flags.replace(/[gy]/g, ''));
}

/**
 * Replace the whole registry with `next`. Called by the host once per plugin
 * manifest refresh, so a disabled plugin's matcher stops firing.
 */
export function replaceContextUrlMatchers(next: ContextUrlMatcherDescriptor[]): void {
  matchers.clear();
  for (const descriptor of next) {
    matchers.set(descriptor.id, { ...descriptor, regex: normalizeRegex(descriptor.regex) });
  }
  rebuildSnapshot();
}

/** Expand `$1`-style group placeholders in a template. */
function expand(template: string, groups: string[]): string {
  return template.replace(/\$(\d)/g, (_, digit: string) => groups[Number(digit) - 1] ?? '');
}

function toMatch(
  matcher: ContextUrlMatcherDescriptor,
  execResult: RegExpExecArray,
): ContextUrlMatch {
  const id = expand(
    matcher.idTemplate,
    execResult.slice(1).map((g) => g ?? ''),
  );
  return { kind: matcher.kind, id, title: id };
}

/** One piece of a split: literal text, or a URL that named a context item. */
export type ContextUrlSegment =
  | { type: 'text'; value: string }
  | ({ type: 'context'; url: string } & ContextUrlMatch);

/** Everything up to whitespace or a delimiter that cannot appear in a URL. */
const URL_TAIL = /^[^\s<>"'`]+/;

/**
 * Punctuation that ends the sentence around a URL, not the URL itself.
 *
 * Trimmed by scanning back from the end rather than with a `[…]+$` regex: the
 * text is a paste, so it is unbounded and attacker-shaped input, and an
 * end-anchored repetition backtracks quadratically over a long run of `!`
 * (CodeQL `js/polynomial-redos`).
 */
const TRAILING_PUNCTUATION = new Set(['.', ',', ';', ':', '!', '?', ')', ']', '}']);

function trimTrailingPunctuation(value: string): string {
  let end = value.length;
  while (end > 0 && TRAILING_PUNCTUATION.has(value[end - 1])) end -= 1;
  return value.slice(0, end);
}

/**
 * The rest of the URL a matcher only identified the head of.
 *
 * A matcher's job is to name the item — for a pull request, `owner/repo#42`.
 * The link in hand usually carries more than that: the tabs and anchors a
 * browser or a review comment adds (`/files`, `.diff`, `#discussion_r123`).
 * That tail belongs to the same URL the chip replaces, so it is consumed with
 * it; otherwise a deep link leaves a `/files#discussion_r123` orphan sitting
 * next to the chip. Handling it here rather than in each plugin's pattern
 * means no matcher has to re-describe URL syntax to avoid that.
 *
 * Sentence punctuation is given back: `see …/pull/42.` ends a sentence.
 */
function urlTail(rest: string): string {
  return trimTrailingPunctuation(URL_TAIL.exec(rest)?.[0] ?? '');
}

/**
 * Split `text` into literal runs and the context references its URLs name.
 * Returns a single text segment when nothing matches, so callers can cheaply
 * detect "no references here" and fall through to their default handling.
 *
 * Matching is leftmost-first across matchers (ties broken by registration
 * order), so an overlapping pair can't reorder the output.
 */
export function splitContextUrls(text: string): ContextUrlSegment[] {
  if (snapshot.length === 0) return [{ type: 'text', value: text }];
  const segments: ContextUrlSegment[] = [];
  let rest = text;
  while (rest) {
    let best: { matcher: ContextUrlMatcherDescriptor; result: RegExpExecArray } | null = null;
    for (const matcher of snapshot) {
      const result = matcher.regex.exec(rest);
      if (!result) continue;
      // A zero-length match would never advance the cursor; ignore it rather
      // than spin. A URL pattern that can match nothing is a plugin bug.
      if (result[0].length === 0) continue;
      if (!best || result.index < best.result.index) best = { matcher, result };
    }
    if (!best) break;
    const { matcher, result } = best;
    if (result.index > 0) segments.push({ type: 'text', value: rest.slice(0, result.index) });
    const matchEnd = result.index + result[0].length;
    const url = result[0] + urlTail(rest.slice(matchEnd));
    segments.push({ type: 'context', url, ...toMatch(matcher, result) });
    rest = rest.slice(result.index + url.length);
  }
  if (rest) segments.push({ type: 'text', value: rest });
  return segments;
}

/** Drop every registered matcher. Test-only. */
export function __resetContextUrlMatchersForTest(): void {
  matchers.clear();
  rebuildSnapshot();
}
